import { useServicesContext } from "@modules/core/services/services.context"
import { OnOffProgramProgressionEntity } from "@modules/programs/entities/onOffProgramProgressionEntity"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

export const useProgramOnOffProgression = (programId?: string, disabled?: boolean) => {
  const { programOnOffApi } = useServicesContext()

  const { data, isLoading } = useSuspenseQuery({
    queryKey: ["programOnOffProgression", programId, disabled],
    queryFn: async () => {
      return programId && !disabled ? await programOnOffApi.getProgressionsProgramOnOff(programId) : null
    },
  })

  return { data, isLoading }
}

export const useProgramOnOffProgressionActions = (programId: string) => {
  const { programOnOffApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: startProgram, isPending: isStartProgramPending } = useMutation({
    mutationFn: async () => {
      const resultDto = await programOnOffApi.startProgramOnOff(programId)
      return OnOffProgramProgressionEntity.fromDto(resultDto)
    },
    onSuccess: async (data: OnOffProgramProgressionEntity) => {
      await queryClient.setQueryData(["programOnOffProgression", programId], data)
      await queryClient.invalidateQueries({ queryKey: ["trainingSessions"] })
      await queryClient.invalidateQueries({ queryKey: ["programBundleSubscription"] })
    },
  })

  const { mutateAsync: stopProgram, isPending: isStopProgramPending } = useMutation({
    mutationFn: async () => {
      const resultDto = await programOnOffApi.stopProgramOnOff(programId)
      return OnOffProgramProgressionEntity.fromDto(resultDto)
    },
    onSuccess: async (data: OnOffProgramProgressionEntity) => {
      await queryClient.setQueryData(["programOnOffProgression", programId], data)
      await queryClient.invalidateQueries({ queryKey: ["trainingSessions"] })
    },
  })

  const { mutateAsync: restartProgram, isPending: isRestartProgramPending } = useMutation({
    mutationFn: async () => {
      const resultDto = await programOnOffApi.restartProgramOnOff(programId)
      return OnOffProgramProgressionEntity.fromDto(resultDto)
    },
    onSuccess: async (data: OnOffProgramProgressionEntity) => {
      await queryClient.setQueryData(["programOnOffProgression", programId], data)
      await queryClient.invalidateQueries({ queryKey: ["trainingSessions"] })
      await queryClient.invalidateQueries({ queryKey: ["programBundleSubscription"] })
    },
  })

  const isUpdating = isStartProgramPending || isStopProgramPending || isRestartProgramPending

  return { isUpdating, startProgram, stopProgram, restartProgram }
}

export const useProgramOnOffSubscription = (programId: string) => {
  const { programOnOffApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: subscribeProgramOnOff, isPending: isSubscribeProgramOnOffPending } = useMutation({
    mutationFn: async () => {
      const resultDto = await programOnOffApi.subscribeProgramOnOff(programId)
      return resultDto.status
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["program", programId] })
      await queryClient.invalidateQueries({ queryKey: ["programs"] })
      await queryClient.invalidateQueries({
        queryKey: ["programOnOffProgression", programId],
      })
    },
  })

  return {
    subscribeProgramOnOff,
    isPending: isSubscribeProgramOnOffPending,
  }
}

export const useProgramOnOffUnsubscription = (programId: string) => {
  const { programOnOffApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: unsubscribeProgramOnOff, isPending: isUnsubscribeProgramOnOffPending } = useMutation({
    mutationFn: async () => {
      const resultDto = await programOnOffApi.unsubscribeProgramOnOff(programId)
      return resultDto.status
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["program", programId] })
      await queryClient.invalidateQueries({ queryKey: ["programs"] })
      await queryClient.invalidateQueries({
        queryKey: ["programOnOffProgression", programId],
      })
    },
  })

  return {
    unsubscribeProgramOnOff,
    isPending: isUnsubscribeProgramOnOffPending,
  }
}

export const useProgramOnOffPublish = () => {
  const { programOnOffApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: publishProgramOnOff, isPending: isPublishProgramOnOffPending } = useMutation({
    mutationFn: async (programId: string) => {
      await programOnOffApi.publishProgramOnOff(programId)
      return programId
    },
    onSuccess: async (programId) => {
      await queryClient.invalidateQueries({ queryKey: ["program", programId] })
      await queryClient.invalidateQueries({ queryKey: ["programs"] })
    },
  })

  return {
    publishProgramOnOff,
    isPending: isPublishProgramOnOffPending,
  }
}
